import type { AttendeePanelProps } from './components/AttendeePanel';
import type { MeetingIconProps } from './components/summary/MeetingIcon';
import type { CollapsedMeetingMessageProps } from './components/summary/CollapsedMeetingMessage';
import type { SenderSummaryProps } from './components/summary/SenderSummary';
import type { PreviewSummaryProps } from './components/summary/PreviewSummary';
import type { MeetingMessageSummaryProps } from './components/summary/MeetingMessageSummary';
import type { MeetingDetailsProps } from './components/MeetingDetails';
import type { MeetingDetailsTimeProps } from './components/MeetingDetailsTime';
import type { MeetingDetailsLocationProps } from './components/MeetingDetailsLocation';
import type { MeetingRequestResponseEditorProps } from './components/MeetingRequestResponseEditor';
import type { MeetingRequestInfoProps } from './components/MeetingRequestInfo';
import type { MeetingMessageInfoProps } from './components/MeetingMessageInfo';
import type { MeetingResponseButtonsProps } from './components/MeetingResponseButtons';
import type { MeetingCancellationActionProperties } from './components/MeetingCancellationAction';
import type * as lazyIndex from './lazyIndex';
import { createLazyComponent, LazyAction, type LazyComponent, LazyModule } from 'owa-bundling';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule(
    () => import(/* webpackChunkName: "MeetingMessage"*/ './lazyIndex'),
    { name: 'MeetingMessage' }
);

// Export types and schema
export type { MeetingRequestViewState } from './store/schema/MeetingRequestViewState';
export { default as deleteMeetingMessage } from './actions/deleteMeetingMessage';
export { onMeetingActionCompleted } from './actions/onMeetingActionCompleted';
export type { MeetingMessageActionType } from './utils/MeetingMessageActionType';
export type { MeetingRequestViewStateHost } from './store/schema/MeetingRequestViewState';

export { default as shouldShowResponseActions } from './utils/shouldShowResponseActions';

// Lazy-load actions
export const lazyAddItemToCalendar: LazyAction<
    typeof lazyIndex.addItemToCalendar,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.addItemToCalendar);

export const lazyInitializeMeetingRequestViewState: LazyAction<
    typeof lazyIndex.initializeMeetingRequestViewStateAction,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.initializeMeetingRequestViewStateAction);

export const lazyGetMeetingSummaryProps: LazyAction<
    typeof lazyIndex.getMeetingSummaryProps,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.getMeetingSummaryProps);

export const lazyRemoveEventOnCancellation: LazyAction<
    typeof lazyIndex.removeEventOnCancellation,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.removeEventOnCancellation);

// Lazy-load components
export const MeetingMessageInfo: LazyComponent<MeetingMessageInfoProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.MeetingMessageInfo);
export const MeetingRequestInfo: LazyComponent<MeetingRequestInfoProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.MeetingRequestInfo);

export const MeetingRequestResponseEditor: LazyComponent<
    MeetingRequestResponseEditorProps,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.MeetingRequestResponseEditor);

export const MeetingDetails: LazyComponent<MeetingDetailsProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.MeetingDetails);
export const MeetingDetailsTime: LazyComponent<MeetingDetailsTimeProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.MeetingDetailsTime);
export const MeetingDetailsLocation: LazyComponent<MeetingDetailsLocationProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.MeetingDetailsLocation);
export const MeetingMessageSummary: LazyComponent<MeetingMessageSummaryProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.MeetingMessageSummary);
export const MeetingResponseButtons: LazyComponent<MeetingResponseButtonsProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.MeetingResponseButtons);
export const MeetingCancellationAction: LazyComponent<
    MeetingCancellationActionProperties,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.MeetingCancellationAction);
export const PreviewSummary: LazyComponent<PreviewSummaryProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.PreviewSummary);
export const SenderSummary: LazyComponent<SenderSummaryProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.SenderSummary
);
export const CollapsedMeetingMessage: LazyComponent<CollapsedMeetingMessageProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.CollapsedMeetingMessage);
export const MeetingIcon: LazyComponent<MeetingIconProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.MeetingIcon
);
export const AttendeePanel: LazyComponent<AttendeePanelProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.AttendeePanel
);

// Lazy-load utilities
export const lazyLogMeetingMessageActionForInvite: LazyAction<
    typeof lazyIndex.logMeetingMessageActionForInvite,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.logMeetingMessageActionForInvite);
export const lazyLogMeetingMessageActionForEvent: LazyAction<
    typeof lazyIndex.logMeetingMessageActionForEvent,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.logMeetingMessageActionForEvent);

export const lazyShowNotificationWhenRespondToMeeting: LazyAction<
    typeof lazyIndex.showNotificationWhenRespondToMeeting,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.showNotificationWhenRespondToMeeting);
